import React, { useState } from "react";
import {
  ArrowLeftIcon,
  CalendarBlankIcon,
  CloudArrowUpIcon,
  FileIcon,
  PlusIcon,
  TrashIcon,
  XIcon,
} from "@phosphor-icons/react";
import {
  ActivityIndicator,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  View,
  type PressableStateCallbackType,
} from "react-native";
import { DatePickerModal } from "react-native-paper-dates";

import { StoragePathType } from "@axio/graphql/generated/graphql";
import CustomButton from "../../../../../components/common/general/CustomButton/CustomButton";
import CustomText from "../../../../../components/common/general/CustomText/CustomText";
import CustomTextInput from "../../../../../components/common/general/CustomTextInput/CustomTextInput";
import CustomRichTextEditor from "../../../../../components/TextEditor/CustomRichTextEditor";
import useFileUpload, { type ProcessedFile } from "../../../../../hooks/useFileUpload";
import { colors } from "../../../../../styles/design-system";
import type { ModernizerCreateActivityInput } from "./ModernizerAddActivityTypes";

interface ModernizerProjectActivityFormProps {
  isSubmitting: boolean;
  onBack: () => void;
  onCancel: () => void;
  onSave: (input: ModernizerCreateActivityInput) => void;
}

interface UploadedProjectFile {
  fileId: string;
  fileName: string;
}

function compactRows(rows: string[]): string[] {
  return rows.map((row) => row.trim()).filter(Boolean);
}

function formatMaxFileSize(maxFileSize: number): string {
  if (maxFileSize <= 0) return "No size limit";
  const sizeMb = Math.ceil(maxFileSize / 1024 / 1024);
  return `${sizeMb}MB max`;
}

function ModernizerProjectActivityForm({ isSubmitting, onBack, onCancel, onSave }: ModernizerProjectActivityFormProps) {
  const [title, setTitle] = useState("");
  const [descriptionHtml, setDescriptionHtml] = useState("");
  const [scopeDocument, setScopeDocument] = useState("");
  const [deliverableTypes, setDeliverableTypes] = useState([""]);
  const [assessmentCriteria, setAssessmentCriteria] = useState([""]);
  const [externalLink, setExternalLink] = useState("");
  const [files, setFiles] = useState<UploadedProjectFile[]>([]);
  const [dueDate, setDueDate] = useState<Date | undefined>();
  const [isDueDatePickerOpen, setIsDueDatePickerOpen] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleFilesUploaded = (processedFiles: ProcessedFile[]) => {
    setFiles((currentFiles) => [
      ...currentFiles,
      ...processedFiles.map(({ file }) => ({ fileId: file.id, fileName: file.fileName })),
    ]);
    setUploadError(null);
    setIsUploading(false);
  };

  const { handleFileUpload, handleServerUploadFromUrl, maxFileSize } = useFileUpload({
    storagePath: StoragePathType.ProjectStarterResources,
    beforeUpload: () => {
      setUploadError(null);
      setIsUploading(true);
    },
    afterUpload: handleFilesUploaded,
    onError: (message) => {
      setIsUploading(false);
      setUploadError(message ?? "File upload failed.");
    },
    multiple: true,
  });

  const handleUploadPress = async () => {
    const processedFiles = await handleFileUpload();
    if (!processedFiles?.length) {
      setIsUploading(false);
    }
  };

  const handleAddExternalLink = async () => {
    const url = externalLink.trim();
    if (!url) return;

    setUploadError(null);
    setIsUploading(true);
    const uploadedFile = await handleServerUploadFromUrl({ url });
    setIsUploading(false);

    if (!uploadedFile?.file) {
      return;
    }

    setFiles((currentFiles) => [
      ...currentFiles,
      { fileId: uploadedFile.file.id, fileName: uploadedFile.file.fileName },
    ]);
    setExternalLink("");
  };

  const handleSubmit = () => {
    const trimmedTitle = title.trim();
    if (!trimmedTitle) return;

    onSave({
      type: "project",
      input: {
        title: trimmedTitle,
        descriptionHtml: descriptionHtml.trim() || undefined,
        scopeDocument: scopeDocument.trim() || undefined,
        deliverableTypes: compactRows(deliverableTypes),
        assessmentCriteria: compactRows(assessmentCriteria),
        files: files.map(({ fileId }) => ({ fileId })),
        dueDate,
      },
    });
  };

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <View style={styles.headerLeft}>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Back to activity types"
            onPress={onBack}
            disabled={isSubmitting || isUploading}
            style={({ hovered }: PressableStateCallbackType) => [styles.iconButton, hovered && styles.iconButtonHover]}>
            <ArrowLeftIcon size={16} color={colors.grey[600]} />
          </Pressable>
          <CustomText
            text="Create Project"
            useTranslationText={false}
            size="s"
            weight="bold"
            style={styles.headerTitle}
          />
        </View>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Close add activity"
          onPress={onCancel}
          disabled={isSubmitting || isUploading}
          style={({ hovered }: PressableStateCallbackType) => [styles.iconButton, hovered && styles.iconButtonHover]}>
          <XIcon size={16} color={colors.grey[400]} />
        </Pressable>
      </View>

      <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollContent} tabIndex={0}>
        <View style={styles.basicsSection}>
          <FieldLabel text="Project Title" />
          <CustomTextInput
            accessibilityLabel="Project title"
            placeholder="Enter project title"
            value={title}
            onChangeText={setTitle}
            editable={!isSubmitting}
            style={styles.textInput}
          />

          <FieldLabel text="Final Due Date" />
          <DateField
            accessibilityLabel="Project due date"
            date={dueDate}
            placeholder="Select final due date"
            disabled={isSubmitting}
            onPress={() => setIsDueDatePickerOpen(true)}
          />
          <DatePickerModal
            locale="en"
            mode="single"
            visible={isDueDatePickerOpen}
            date={dueDate}
            onDismiss={() => setIsDueDatePickerOpen(false)}
            onConfirm={({ date }) => {
              setDueDate(date);
              setIsDueDatePickerOpen(false);
            }}
            animationType={Platform.OS === "web" ? "none" : "slide"}
          />

          <FieldLabel text="Project Overview" />
          <View style={styles.editor}>
            <CustomRichTextEditor
              value={descriptionHtml}
              onChange={setDescriptionHtml}
              isSubmitting={isSubmitting}
              placeholder="Describe the project and its goals..."
              activelyEditing
              restrictImageUpload
            />
          </View>
        </View>

        <FormSection title="Scope">
          <CustomTextInput
            accessibilityLabel="Scope document"
            placeholder="What's in scope for this project, and what's explicitly out of scope..."
            value={scopeDocument}
            onChangeText={setScopeDocument}
            editable={!isSubmitting}
            multiline
            numberOfLines={4}
            style={styles.multilineInput}
          />
        </FormSection>

        <FormSection title="Evaluation Criteria">
          <RepeatableRows
            rows={assessmentCriteria}
            accessibilityLabel="Assessment criterion"
            placeholder="Criterion"
            addLabel="Add Criterion"
            disabled={isSubmitting}
            onChange={setAssessmentCriteria}
          />
        </FormSection>

        <FormSection title="Deliverables">
          <DeliverableRows rows={deliverableTypes} disabled={isSubmitting} onChange={setDeliverableTypes} />
        </FormSection>

        <FormSection title="Supporting Materials">
          <FieldLabel text="Files" />
          <CustomText
            text="Optional - examples, templates, reference docs"
            useTranslationText={false}
            size="xs"
            style={styles.helperText}
          />
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Upload project files"
            onPress={handleUploadPress}
            disabled={isSubmitting || isUploading}
            style={({ hovered }: PressableStateCallbackType) => [
              styles.uploadBox,
              hovered && !isUploading && styles.uploadBoxHover,
            ]}>
            {isUploading ? (
              <ActivityIndicator size="small" color={colors.brand[500]} />
            ) : (
              <CloudArrowUpIcon size={28} color={colors.brand[500]} />
            )}
            <CustomText
              text={isUploading ? "Uploading..." : "Drag & drop or browse"}
              useTranslationText={false}
              size="xs"
              style={styles.uploadText}
            />
            <CustomText
              text={formatMaxFileSize(maxFileSize)}
              useTranslationText={false}
              size="xs"
              style={styles.uploadLimit}
            />
          </Pressable>
          {uploadError ? (
            <CustomText text={uploadError} useTranslationText={false} size="xs" style={styles.errorText} />
          ) : null}
          {files.map((file) => (
            <View key={file.fileId} style={styles.fileRow}>
              <FileIcon size={16} color={colors.grey[600]} />
              <CustomText text={file.fileName} useTranslationText={false} size="xs" style={styles.fileName} />
              <Pressable
                accessibilityRole="button"
                accessibilityLabel={`Remove ${file.fileName}`}
                onPress={() => setFiles((currentFiles) => currentFiles.filter(({ fileId }) => fileId !== file.fileId))}
                disabled={isSubmitting}>
                <TrashIcon size={14} color={colors.grey[500]} />
              </Pressable>
            </View>
          ))}
          <FieldLabel text="External Links" />
          <View style={styles.externalLinkRow}>
            <CustomTextInput
              accessibilityLabel="External link"
              placeholder="Paste a URL..."
              value={externalLink}
              onChangeText={setExternalLink}
              editable={!isSubmitting && !isUploading}
              autoCapitalize="none"
              style={styles.textInput}
            />
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Add external link"
              onPress={handleAddExternalLink}
              disabled={isSubmitting || isUploading || !externalLink.trim()}
              style={styles.externalLinkButton}>
              <PlusIcon size={14} color={colors.grey[500]} />
            </Pressable>
          </View>
        </FormSection>
      </ScrollView>

      <View style={styles.footer}>
        <CustomButton
          text="Cancel"
          size="s"
          styleType="primaryTransparentStroke"
          onPress={onCancel}
          disabled={isSubmitting || isUploading}
          useTranslationText={false}
        />
        <CustomButton
          text="Create Project"
          accessibilityLabel="Create project"
          size="s"
          styleType="primary"
          onPress={handleSubmit}
          disabled={isSubmitting || isUploading || !title.trim()}
          useTranslationText={false}
        />
      </View>
    </View>
  );
}

function DeliverableRows({
  rows,
  disabled,
  onChange,
}: {
  rows: string[];
  disabled: boolean;
  onChange: (rows: string[]) => void;
}) {
  const updateRow = (index: number, value: string) => {
    onChange(rows.map((row, rowIndex) => (rowIndex === index ? value : row)));
  };

  const addRow = () => {
    onChange([...rows, ""]);
  };

  return (
    <>
      {rows.map((row, index) => (
        <View key={index} style={styles.deliverableRow}>
          <CustomTextInput
            accessibilityLabel={`Deliverable ${index + 1}`}
            placeholder={`Deliverable ${index + 1} (e.g., Proposal)`}
            value={row}
            onChangeText={(value) => updateRow(index, value)}
            editable={!disabled}
            style={styles.textInput}
          />
        </View>
      ))}
      <Pressable
        accessibilityRole="button"
        accessibilityLabel="Add Deliverable"
        onPress={addRow}
        disabled={disabled}
        style={styles.addRowButton}>
        <PlusIcon size={12} color={colors.brand[500]} weight="bold" />
        <CustomText text="Add Deliverable" useTranslationText={false} size="xs" style={styles.addRowText} />
      </Pressable>
    </>
  );
}

function FormSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <View style={styles.section}>
      <CustomText
        text={title.toUpperCase()}
        useTranslationText={false}
        size="xs"
        weight="bold"
        style={styles.sectionTitle}
      />
      {children}
    </View>
  );
}

function FieldLabel({ text }: { text: string }) {
  return <CustomText text={text} useTranslationText={false} size="xs" style={styles.fieldLabel} />;
}

function RepeatableRows({
  rows,
  accessibilityLabel,
  placeholder,
  addLabel,
  disabled,
  onChange,
}: {
  rows: string[];
  accessibilityLabel: string;
  placeholder: string;
  addLabel: string;
  disabled: boolean;
  onChange: (rows: string[]) => void;
}) {
  const updateRow = (index: number, value: string) => {
    onChange(rows.map((row, rowIndex) => (rowIndex === index ? value : row)));
  };

  const removeRow = (index: number) => {
    onChange(rows.length === 1 ? [""] : rows.filter((_, rowIndex) => rowIndex !== index));
  };

  return (
    <>
      {rows.map((row, index) => (
        <View key={index} style={styles.repeatableRow}>
          <CustomTextInput
            accessibilityLabel={`${accessibilityLabel} ${index + 1}`}
            placeholder={`${placeholder} ${index + 1}`}
            value={row}
            onChangeText={(value) => updateRow(index, value)}
            editable={!disabled}
            style={styles.textInput}
          />
          {rows.length > 1 ? (
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={`Remove ${accessibilityLabel.toLowerCase()} ${index + 1}`}
              onPress={() => removeRow(index)}
              disabled={disabled}
              style={styles.removeRowButton}>
              <XIcon size={14} color={colors.grey[500]} />
            </Pressable>
          ) : null}
        </View>
      ))}
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={addLabel}
        onPress={() => onChange([...rows, ""])}
        disabled={disabled}
        style={styles.addRowButton}>
        <PlusIcon size={12} color={colors.brand[500]} weight="bold" />
        <CustomText text={addLabel} useTranslationText={false} size="xs" style={styles.addRowText} />
      </Pressable>
    </>
  );
}

function DateField({
  accessibilityLabel,
  date,
  placeholder,
  disabled,
  onPress,
  compact = false,
}: {
  accessibilityLabel: string;
  date?: Date;
  placeholder: string;
  disabled: boolean;
  onPress: () => void;
  compact?: boolean;
}) {
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      onPress={onPress}
      disabled={disabled}
      style={[styles.dateInput, compact && styles.compactDateInput, disabled && styles.disabled]}>
      <CalendarBlankIcon size={14} color={colors.grey[600]} />
      <CustomText
        text={date ? date.toLocaleDateString("en-US") : placeholder}
        useTranslationText={false}
        size="xs"
        style={date ? styles.dateValue : styles.placeholder}
      />
    </Pressable>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    height: "100%",
    backgroundColor: colors.white,
  },
  header: {
    minHeight: 56,
    paddingHorizontal: 20,
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  headerLeft: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  headerTitle: {
    color: colors.grey[900],
  },
  iconButton: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: "center",
    justifyContent: "center",
  },
  iconButtonHover: {
    backgroundColor: colors.grey[100],
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: 20,
    paddingBottom: 28,
  },
  basicsSection: {
    paddingTop: 16,
    paddingBottom: 20,
  },
  section: {
    paddingVertical: 24,
    borderTopWidth: 1,
    borderTopColor: colors.grey[200],
  },
  sectionTitle: {
    color: colors.grey[500],
    fontSize: 10,
    letterSpacing: 0.5,
    marginBottom: 14,
  },
  fieldLabel: {
    color: colors.grey[700],
    marginBottom: 7,
    marginTop: 14,
  },
  textInput: {
    minHeight: 38,
    paddingVertical: 8,
    paddingHorizontal: 10,
    borderRadius: 7,
    fontSize: 12,
    outlineWidth: 0,
  },
  multilineInput: {
    minHeight: 96,
    paddingVertical: 10,
    paddingHorizontal: 10,
    borderRadius: 7,
    fontSize: 12,
    textAlignVertical: "top",
    outlineWidth: 0,
  },
  editor: {
    minHeight: 150,
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderRadius: 12,
    overflow: "hidden",
  },
  repeatableRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 8,
  },
  deliverableRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 8,
  },
  removeRowButton: {
    width: 32,
    height: 32,
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderRadius: 7,
  },
  addRowButton: {
    flexDirection: "row",
    alignItems: "center",
    alignSelf: "flex-start",
    gap: 4,
    paddingVertical: 3,
  },
  addRowText: {
    color: colors.brand[500],
    fontSize: 11,
  },
  helperText: {
    color: colors.grey[500],
    fontSize: 10,
    marginBottom: 10,
  },
  uploadBox: {
    minHeight: 136,
    borderWidth: 1,
    borderStyle: "dashed",
    borderColor: colors.brand[300],
    borderRadius: 12,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: colors.white,
    gap: 4,
  },
  uploadBoxHover: {
    backgroundColor: colors.brand[25],
    borderColor: colors.brand[500],
  },
  uploadText: {
    color: colors.grey[700],
    fontSize: 11,
  },
  uploadLimit: {
    color: colors.brand[500],
    fontSize: 10,
  },
  errorText: {
    color: colors.error[600],
    marginTop: 8,
  },
  fileRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginTop: 8,
    padding: 10,
    borderRadius: 7,
    borderWidth: 1,
    borderColor: colors.grey[200],
  },
  fileName: {
    flex: 1,
    color: colors.grey[700],
  },
  dateInput: {
    minHeight: 38,
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    paddingHorizontal: 12,
    borderWidth: 1,
    borderColor: colors.grey[300],
    borderRadius: 7,
    backgroundColor: colors.white,
  },
  compactDateInput: {
    width: 142,
  },
  disabled: {
    opacity: 0.5,
  },
  dateValue: {
    color: colors.grey[900],
  },
  placeholder: {
    color: colors.grey[600],
  },
  externalLinkRow: {
    flexDirection: "row",
    gap: 8,
  },
  externalLinkButton: {
    width: 38,
    height: 38,
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderRadius: 7,
  },
  footer: {
    minHeight: 64,
    paddingHorizontal: 28,
    borderTopWidth: 1,
    borderTopColor: colors.grey[200],
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: colors.white,
  },
});

export default ModernizerProjectActivityForm;
